/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { TextInput, View } from "react-native";
import { Prompt } from "react-router";
import { useLazyQuery, useMutation } from "@apollo/client";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import {
  BackExploreBar,
  Button,
  ConfirmDiscardModal,
  DescriptionTextField,
  LoadingView,
  Text,
  styles,
} from "UpduoLibrary";
import {
  skaleRed,
  skaleWhite,
  skaleWebBackgroundGray,
  skalePrimaryBlue,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import { useEventListener } from "config/Hooks";
import { GET_SKILL_MODULE_DETAIL } from "graphql/internal/Queries";
import {
  CREATE_SKILL_MODULE,
  EDIT_SKILL_MODULE,
} from "graphql/internal/Mutations";
import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function SkillModuleDetailRoute({ match, history }) {
  // The skill detail/editing route component

  const dispatch = useDispatch();
  const authToken = useSelector((state: RootState) => state.authToken);
  const skillModuleName = useSelector(
    (state: RootState) => state.skillModuleName
  );
  const skillModuleDescription = useSelector(
    (state: RootState) => state.skillModuleDescription
  );
  const selfObject = useSelector((state: RootState) => state.selfObject);

  const [isLoading, setIsLoading] = useState(true);
  const [alertColor, setAlertColor] = useState("green");
  const [alertString, setAlertString] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds =
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
  const isLibraryOrganization = libraryOrganizationIds.includes(
    selfObject?.organizationId
  );
  const canEdit =
    (isLibraryOrganization && selfObject.storeName === "Maintenance") ||
    !isLibraryOrganization;

  const [getSkillModuleDetail, {}] = useLazyQuery(GET_SKILL_MODULE_DETAIL, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.skillModule) {
        dispatch(
          ActionCreators.setSkillModuleName(
            data?.skillModule?.name + (isNew ? " [COPY]" : "")
          )
        );
        dispatch(
          ActionCreators.setSkillModuleDescription(
            data?.skillModule?.description
          )
        );
        setIsLoading(false);
      }
    },
    onError: (error) => {
      console.error("ERROR getSkillModuleDetail", error);
      UpduoAnalytics.logEvent(
        "app-skillmoduledetail-getskillmoduledetail-error",
        {
          error,
        }
      );
      history.goBack();
    },
  });

  const [createSkillModule, {}] = useMutation(CREATE_SKILL_MODULE, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      history.push("/content");
    },
    onError: (error) => {
      console.error("ERROR createSkillModule", error);
      UpduoAnalytics.logEvent("app-skillmoduledetail-createskillmodule-error", {
        error,
      });
      history.goBack();
    },
  });

  const [editSkillModule, {}] = useMutation(EDIT_SKILL_MODULE, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      setIsLoading(false);
      setAlertColor("green");
      setAlertString("Your changes are saved!");
    },
    onError: (error) => {
      console.error("ERROR editSkillModule", error);
      UpduoAnalytics.logEvent("app-skillmoduledetail-editskillmodule-error", {
        error,
      });
    },
  });

  const isNew = match.params.idHash === "new";

  const _handleShowUnsavedWarning = () => {
    if (canEdit) {
      setAlertColor(skaleRed);
      setAlertString("You have unsaved changes.");
    }
  };

  const _handleSetCardWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  const _handleGetSkillModuleDetail = useCallback(
    (skillModuleId) => {
      setIsLoading(true);
      getSkillModuleDetail({
        variables: {
          getSkillModuleDetailRequest: {
            skillModuleId: skillModuleId,
          },
        },
      });
    },
    [dispatch, history, isNew]
  );

  useEffect(() => {
    dispatch(ActionCreators.setSkillModuleName("New Topic"));
    dispatch(
      ActionCreators.setSkillModuleDescription(
        "<ul><li>In this topic, you'll practice many important lessons.</li></ul>"
      )
    );
    // Get skill module details based on skill module ID
    if (isNew) {
      if (match.params.cloneIdHash) {
        _handleGetSkillModuleDetail(match.params.cloneIdHash);
      } else {
        setIsLoading(false);
      }
    } else {
      _handleGetSkillModuleDetail(match.params.idHash);
    }
    _handleSetCardWidth();
    window.addEventListener("resize", _handleSetCardWidth);
    return () => {
      window.removeEventListener("resize", _handleSetCardWidth);
    };
  }, [
    authToken,
    dispatch,
    isNew,
    match.params.cloneIdHash,
    match.params.idHash,
    _handleGetSkillModuleDetail,
  ]);

  const _handleEditDescription = (newSkillModuleDescription) => {
    if (newSkillModuleDescription === skillModuleDescription) {
      return;
    }
    _handleShowUnsavedWarning();
    dispatch(
      ActionCreators.setSkillModuleDescription(newSkillModuleDescription)
    );
  };

  const _handleSave = useCallback(() => {
    UpduoAnalytics.logEvent("user-skill-module-detail-save", {
      skillModuleID: match.params.idHash,
    });
    if (!canEdit) {
      setAlertColor(skaleRed);
      setAlertString("Topic editing is currently disabled.");
      return;
    }
    const descriptionIsInvalid =
      skillModuleDescription === "<ul><li></li></ul>";
    if (!skillModuleName || descriptionIsInvalid) {
      setAlertColor(skaleRed);
      setAlertString(
        "Empty fields are not allowed. Please add content to empty fields and/or delete unused cards."
      );
      return;
    }
    const skillModuleDescriptionString = skillModuleDescription;
    const skillModuleNameString = skillModuleName;
    const skillModuleAttributes = {
      description: skillModuleDescriptionString,
      name: skillModuleNameString,
    };
    setIsLoading(true);
    if (isNew) {
      createSkillModule({
        variables: {
          createSkillModuleRequest: {
            ...skillModuleAttributes,
          },
        },
      });
    } else {
      editSkillModule({
        variables: {
          editSkillModuleRequest: {
            ...skillModuleAttributes,
            skillModuleId: match.params.idHash,
          },
        },
      });
    }
  }, [
    authToken,
    skillModuleName,
    skillModuleDescription,
    history,
    isNew,
    match.params.idHash,
    canEdit,
  ]);

  const _handleKeyDown = useCallback(
    (e) => {
      const regex = /Mac/;
      const isMac = regex.exec(navigator.platform);
      if (e.keyCode === 83 && (isMac ? e.metaKey : e.ctrlKey)) {
        // Allow save keyboard shortcut to work
        e.preventDefault();
        _handleSave();
      }
    },
    [_handleSave]
  );

  useEventListener("keydown", _handleKeyDown);

  const _handleDiscardEditsButtonPress = () => {
    UpduoAnalytics.logEvent("user-skill-module-detail-edit-toggle", {
      skillModuleId: match.params.idHash,
      isChanged: alertString ? true : false,
    });
    if (alertString) {
      setIsDiscardModalVisible(true);
      return;
    }
    if (isNew) {
      history.push("/content");
      return;
    }
    setAlertString("");
  };

  const _handleCancelDiscard = () => {
    UpduoAnalytics.logEvent("user-skill-module-detail-discard-cancel", {
      skillModuleId: match.params.idHash,
    });
    setIsDiscardModalVisible(false);
  };

  const _handleConfirmDiscard = () => {
    UpduoAnalytics.logEvent("user-skill-module-detail-discard-confirm", {
      skillModuleId: match.params.idHash,
    });
    if (isNew) {
      history.push("/content");
    } else {
      _handleGetSkillModuleDetail(match.params.idHash);
      setAlertString("");
      setIsDiscardModalVisible(false);
    }
  };

  if (!isLoading) {
    const saveButton = (
      <Button
        marginLeft={20}
        width={260}
        height={36}
        borderColor={skalePrimaryBlue}
        onPress={() => {
          _handleSave();
        }}
        text={isNew ? "Save New Topic" : "Save Changes"}
      />
    );
    const editToggleButton = (
      <Button
        width={170}
        height={36}
        borderWidth={1}
        borderColor={skaleRed}
        color={skaleWhite}
        textColor={skaleRed}
        onPress={() => {
          _handleDiscardEditsButtonPress();
        }}
        text={"Discard Edits"}
      />
    );
    const skillModuleEditingHeading = isNew ? "Create Topic" : "Edit Topic";
    const skillModuleTitle = skillModuleName ? skillModuleName : "Untitled";
    const skillModuleHeading = `${skillModuleEditingHeading} - ${skillModuleTitle}`;
    const editControls = (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {editToggleButton}
        {saveButton}
      </View>
    );
    const descriptionView = (
      <View
        style={{
          width: Math.min(windowWidth * 0.9, 1800),
          minWidth: 1000,
        }}
      >
        <View
          style={{
            backgroundColor: skaleWhite,
            borderWidth: 1,
            borderColor: skaleWebBackgroundGray,
            padding: 60,
            borderRadius: 10,
            marginBottom: 20,
            paddingRight: 400,
          }}
        >
          <Text
            style={[
              styles.titleEditText,
              {
                marginBottom: 8,
              },
            ]}
          >
            {"Topic Name"}
          </Text>
          <TextInput
            style={styles.skillInput}
            value={skillModuleName}
            onChangeText={(text) => {
              dispatch(ActionCreators.setSkillModuleName(text));
              _handleShowUnsavedWarning();
            }}
          />

          <Text
            style={[
              styles.titleEditText,
              {
                marginTop: 30,
                marginBottom: 8,
              },
            ]}
          >
            {"Topic Description"}
          </Text>
          <DescriptionTextField
            description={skillModuleDescription}
            onEdit={(newSkillModuleDescription) => {
              _handleEditDescription(newSkillModuleDescription);
            }}
          />
        </View>
      </View>
    );

    return (
      <View style={{ flex: 1 }}>
        <BackExploreBar
          previousTitle={"Content"}
          onBack={() => {
            UpduoAnalytics.logEvent("user-skill-module-detail-back", {
              skillModuleId: match.params.idHash,
            });
            history.push("/content");
          }}
        />
        <View style={styles.routeBodyContainer}>
          <View
            style={{
              zIndex: 5,
              paddingVertical: 10,
              justifyContent: "center",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                paddingHorizontal: 15,
                justifyContent: "space-between",
              }}
            >
              <View style={{ alignSelf: "center", justifyContent: "center" }}>
                <Text
                  style={{
                    fontSize: 24,
                    fontWeight: "bold",
                    color: upduoPrimaryBlack,
                    flex: 1,
                    flexWrap: "wrap",
                    maxWidth: window.innerWidth * 0.5 - 50,
                  }}
                >
                  {skillModuleHeading}
                </Text>
              </View>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text
                  style={{
                    color: alertColor,
                    marginRight: 20,
                  }}
                >
                  {alertString}
                </Text>
                {alertString && alertColor === skaleRed ? editControls : null}
              </View>
            </View>
          </View>
          <View style={{ alignItems: "center", marginTop: 32 }}>
            {descriptionView}
          </View>
        </View>
        <ConfirmDiscardModal
          visible={isDiscardModalVisible}
          onCancelDiscard={_handleCancelDiscard}
          onConfirmDiscard={_handleConfirmDiscard}
        />
        <Prompt
          when={alertColor === skaleRed && !!alertString}
          message={"You have unsaved changes, are you sure you want to leave?"}
        />
      </View>
    );
  } else {
    return <LoadingView />;
  }
}
